/**
 * PDP Gallery — Flickity-powered product media gallery
 *
 * Custom element <pdp-gallery> that initializes Flickity carousels
 * for main viewer, thumbnails, and lightbox.
 *
 * Supports two modes:
 * - "standard": All product.media shown (replaces Dawn's slider-component)
 * - "per-color": Only metaobject images for the selected color
 */

if (!customElements.get('pdp-gallery')) {
  customElements.define(
    'pdp-gallery',
    class PdpGallery extends HTMLElement {
      constructor() {
        super();
        this.mainFlkty = null;
        this.thumbFlkty = null;
        this.lightboxFlkty = null;

        this.galleryMode = this.dataset.galleryMode || 'standard';
        this.currentColorId = this.dataset.currentColorId || '';
        this.sectionId = this.dataset.sectionId || '';
        this.mobileLayout = this.dataset.mobileLayout || '';
      }

      connectedCallback() {
        // Wait for Flickity to be available
        if (typeof Flickity === 'undefined') {
          this._onLoad = () => this.init();
          window.addEventListener('load', this._onLoad);
          return;
        }
        this.init();
      }

      init() {
        const mainEl = this.querySelector('.pdp-gallery__main');
        const thumbEl = this.querySelector('.pdp-gallery__thumbnails');
        if (mainEl) mainEl.classList.remove('hidden');
        if (thumbEl) thumbEl.classList.remove('hidden');

        this.initMain();
        this.initThumbnails();
        this.bindEvents();

        const skeleton = this.querySelector('.pdp-gallery__skeleton');
        const skeletonThumbs = this.querySelector('.pdp-gallery__skeleton-thumbs');
        if (skeleton) skeleton.classList.add('hidden');
        if (skeletonThumbs) skeletonThumbs.classList.add('hidden');
      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        if (this._onLoad) {
          window.removeEventListener('load', this._onLoad);
          this._onLoad = null;
        }
        this._destroyFlickityInstances();
        // Return lightbox from body if it was moved there
        const bodyLightbox = document.body.querySelector('.pdp-lightbox');
        if (bodyLightbox && this._lightboxPlaceholder && this._lightboxPlaceholder.parentNode) {
          this._lightboxPlaceholder.parentNode.insertBefore(bodyLightbox, this._lightboxPlaceholder);
          this._lightboxPlaceholder.remove();
        }
        this.removeEventListeners();
      }

      _destroyFlickityInstances() {
        if (this.mainFlkty) { this.mainFlkty.destroy(); this.mainFlkty = null; }
        if (this.thumbFlkty) { this.thumbFlkty.destroy(); this.thumbFlkty = null; }
        if (this.lightboxFlkty) { this.lightboxFlkty.destroy(); this.lightboxFlkty = null; }
      }

      /* ============================
         Main Carousel
         ============================ */

      initMain() {
        const mainEl = this.querySelector('.pdp-gallery__main');
        if (!mainEl || mainEl.children.length === 0) return;

        const useDots = this.mobileLayout === 'dots';

        this.mainFlkty = new Flickity(mainEl, {
          cellAlign: 'left',
          contain: true,
          prevNextButtons: true,
          pageDots: useDots,
          wrapAround: false,
          draggable: true,
          imagesLoaded: true,
          adaptiveHeight: false,
          accessibility: true,
          setGallerySize: true,
        });

        // Initial arrow + overlay state
        this.updateArrows();
        this.updateOverlay(0);

        this.mainFlkty.on('change', (index) => {
          this.updateArrows();
          this.updateOverlay(index);
          this.updateGalleryStatus(index);
        });
      }

      updateArrows() {
        if (!this.mainFlkty) return;
        const index = this.mainFlkty.selectedIndex;
        const prevBtn = this.querySelector('.pdp-gallery__main .flickity-prev-next-button.previous');
        const nextBtn = this.querySelector('.pdp-gallery__main .flickity-prev-next-button.next');

        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index >= this.mainFlkty.slides.length - 1;
      }

      updateOverlay(index) {
        const overlays = this.querySelectorAll('.pdp-gallery__main [data-overlay]');
        overlays.forEach((el, i) => {
          el.classList.toggle('is-visible', i === index);
        });
      }

      updateGalleryStatus(index) {
        const statusEl = this.querySelector(`#GalleryStatus-${this.sectionId}`);
        if (!statusEl || !this.mainFlkty) return;
        statusEl.textContent = `Image ${index + 1} of ${this.mainFlkty.slides.length}`;
      }

      /* ============================
         Thumbnails
         ============================ */

      initThumbnails() {
        const thumbEl = this.querySelector('.pdp-gallery__thumbnails');
        if (!thumbEl || thumbEl.children.length <= 1) return;

        const mainSelector = `#${CSS.escape('PdpGalleryMain-' + this.sectionId)}`;

        this.thumbFlkty = new Flickity(thumbEl, {
          asNavFor: mainSelector,
          contain: true,
          pageDots: false,
          prevNextButtons: false,
          cellAlign: 'left',
          draggable: true,
        });
      }

      /* ============================
         Lightbox
         ============================ */

      openLightbox(index) {
        const lightbox = this.querySelector('.pdp-lightbox') || document.body.querySelector('.pdp-lightbox');
        if (!lightbox) return;

        // Move lightbox to body to escape any stacking context (scroll-trigger transforms, etc.)
        if (lightbox.parentElement !== document.body) {
          this._lightboxPlaceholder = document.createComment('pdp-lightbox-placeholder');
          lightbox.parentElement.insertBefore(this._lightboxPlaceholder, lightbox);
          document.body.appendChild(lightbox);
        }

        lightbox.style.display = '';
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pdp-lightbox-open');

        const carouselEl = lightbox.querySelector('.pdp-lightbox__carousel');
        if (!carouselEl || carouselEl.children.length === 0) return;

        if (!this.lightboxFlkty) {
          this.lightboxFlkty = new Flickity(carouselEl, {
            wrapAround: true,
            prevNextButtons: true,
            pageDots: false,
            draggable: true,
            imagesLoaded: true,
            initialIndex: index || 0,
            setGallerySize: true,
          });
        } else {
          this.lightboxFlkty.select(index || 0, false, true);
          this.lightboxFlkty.resize();
        }

        // Focus trap + keyboard navigation
        lightbox.focus();
        this._onTrapFocus = (e) => this._trapFocus(e);
        document.addEventListener('keydown', this._onTrapFocus);
      }

      closeLightbox() {
        const lightbox = document.body.querySelector('.pdp-lightbox') || this.querySelector('.pdp-lightbox');
        if (!lightbox) return;

        lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pdp-lightbox-open');

        if (this._onTrapFocus) {
          document.removeEventListener('keydown', this._onTrapFocus);
          this._onTrapFocus = null;
        }

        // Return focus to the zoom button that opened the lightbox
        const activeOverlay = this.querySelector('.pdp-gallery__overlay.is-visible');
        if (activeOverlay) {
          const zoomBtn = activeOverlay.querySelector('.pdp-gallery__zoom-btn');
          if (zoomBtn) zoomBtn.focus();
        }
      }

      /* ============================
         Events
         ============================ */

      _getLightbox() {
        return this.querySelector('.pdp-lightbox') || document.body.querySelector('.pdp-lightbox');
      }

      _trapFocus(e) {
        if (e.key !== 'Tab') return;
        const lightbox = this._getLightbox();
        if (!lightbox) return;

        const focusable = lightbox.querySelectorAll('button:not([disabled])');
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      bindEvents() {
        // Zoom button clicks -> open lightbox
        this._onZoomClick = (e) => {
          const btn = e.target.closest('.pdp-gallery__zoom-btn');
          if (!btn) return;
          const slideIndex = parseInt(btn.dataset.slideIndex, 10) || 0;
          this.openLightbox(slideIndex);
        };
        this.addEventListener('click', this._onZoomClick);

        // Lightbox close via close button, backdrop, or clicking outside the image
        // Listen on document because lightbox is moved to body
        this._onLightboxClose = (e) => {
          const lightbox = this._getLightbox();
          if (!lightbox || lightbox.getAttribute('aria-hidden') !== 'false') return;

          // Close button or backdrop clicked
          if (e.target.closest('[data-lightbox-close]')) {
            this.closeLightbox();
            return;
          }

          // Click inside lightbox but NOT on image, arrows, or dots — close
          if (
            e.target.closest('.pdp-lightbox') &&
            !e.target.closest('.pdp-lightbox__slide img') &&
            !e.target.closest('.flickity-prev-next-button')
          ) {
            this.closeLightbox();
          }
        };
        document.addEventListener('click', this._onLightboxClose);

        // Escape key closes lightbox
        this._onKeydown = (e) => {
          if (e.key === 'Escape') {
            const lightbox = this._getLightbox();
            if (lightbox && lightbox.getAttribute('aria-hidden') === 'false') {
              this.closeLightbox();
            }
          }
        };
        document.addEventListener('keydown', this._onKeydown);
      }

      removeEventListeners() {
        if (this._onZoomClick) this.removeEventListener('click', this._onZoomClick);
        if (this._onLightboxClose) document.removeEventListener('click', this._onLightboxClose);
        if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
        if (this._onTrapFocus) document.removeEventListener('keydown', this._onTrapFocus);
      }

      /* ============================
         Variant Change Handling
         ============================ */

      handleVariantChange(html, variantFeaturedMediaId) {
        console.log('DEBUG handleVariantChange called, mode:', this.galleryMode);
        if (this.galleryMode === 'per-color') {
          this.handlePerColorVariantChange(html);
        } else {
          this.handleStandardVariantChange(html, variantFeaturedMediaId);
        }
      }

      handlePerColorVariantChange(html) {
        const newGallery = html.querySelector('pdp-gallery');
        console.log('DEBUG perColor: newGallery found?', !!newGallery);
        if (!newGallery) return;

        const newColorId = newGallery.dataset.currentColorId;

        console.log('DEBUG perColor: newColorId =', newColorId, '| currentColorId =', this.currentColorId);
        // If color hasn't changed (size change, etc.) — leave gallery untouched
        if (newColorId === this.currentColorId) {
          console.log('DEBUG perColor: same color, skipping');
          return;
        }

        // Color changed — rebuild entire gallery
        this.currentColorId = newColorId;
        this.dataset.currentColorId = newColorId;
        this.rebuildGallery(newGallery);
      }

      handleStandardVariantChange(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId || !this.mainFlkty) return;
        const targetSlide = this.querySelector(
          `.pdp-gallery__main [data-media-id="${this.sectionId}-${variantFeaturedMediaId}"]`
        );
        if (targetSlide) {
          const index = parseInt(targetSlide.dataset.index, 10);
          this.mainFlkty.select(index);
        }
      }

      rebuildGallery(newGalleryElement) {
        // Close lightbox if open (prevents stale UI after rebuild)
        this.closeLightbox();

        this._destroyFlickityInstances();

        // Replace content
        const newMain = newGalleryElement.querySelector('.pdp-gallery__main');
        const newThumbs = newGalleryElement.querySelector('.pdp-gallery__thumbnails');
        const newLightboxCarousel = newGalleryElement.querySelector('.pdp-lightbox__carousel');

        const mainEl = this.querySelector('.pdp-gallery__main');
        const thumbEl = this.querySelector('.pdp-gallery__thumbnails');
        const lightbox = this._getLightbox();
        const lightboxCarouselEl = lightbox ? lightbox.querySelector('.pdp-lightbox__carousel') : null;

        if (mainEl && newMain) mainEl.innerHTML = newMain.innerHTML;
        if (thumbEl && newThumbs) thumbEl.innerHTML = newThumbs.innerHTML;
        if (lightboxCarouselEl && newLightboxCarousel) {
          lightboxCarouselEl.innerHTML = newLightboxCarousel.innerHTML;
        }

        // Update single-image attribute
        const isSingle = newGalleryElement.dataset.singleImage === 'true';
        if (isSingle) {
          this.dataset.singleImage = 'true';
        } else {
          delete this.dataset.singleImage;
        }

        // Re-initialize
        this.initMain();
        this.initThumbnails();
        // Lightbox re-inits lazily on next open
      }
    }
  );
}
